/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LeavesList from "./LeavesList";
import { Row } from "components/DataTable";
import leaveManager from "services/Leave/leaveManager";
import { AiOutlineExport } from "react-icons/ai";
import { FaEdit, FaEye, FaRegTrashAlt, FaExclamationTriangle } from "react-icons/fa";
import Modal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
import GenericModal from "components/Modal";
import GenericToaster from "components/Toaster";
import ViewLeave from "./ViewLeave/ViewLeave";
import AddLeave from "./AddLeave/AddLeave";
import LeaveDetails from "./LeaveDetails";
import { useTranslation } from "react-i18next";
import apiService from "services/apiService";
import { useAppContext } from "contexts";
import { storageService } from "services/storageService";
import HeaderButtons from "components/Button/HeaderButtons";
import { InputActionMeta } from "react-select";
import { sortByKey } from "util/sortOptions";
import { dateFormate } from "util/Util";
import * as XLSX from "xlsx";
import CommonListingFilter, {
  CommonFilterField,
  CommonFilterOption,
} from "components/CommonListingFilter/CommonListingFilter";

interface OptionProps {
  value: string;
  label: string;
}

interface UserSummary {
  email?: string;
  displayName?: string;
}

/**
 * Amber balance-warning icon shown on a leave row. The message is rendered in a
 * portal with `position: fixed`, so it escapes the table's `overflow-auto`
 * scroll container (an in-cell absolute tooltip would be clipped by other rows).
 */
const BALANCE_TOOLTIP_WIDTH = 340;

const BalanceWarningBadge: React.FC<{ message: string }> = ({ message }) => {
  // tip holds the resolved, viewport-clamped placement (null = hidden).
  const [tip, setTip] = useState<
    { top: number; left: number; width: number; caretLeft: number } | null
  >(null);
  const iconRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;

    const margin = 8;
    const viewportWidth = window.innerWidth;
    const width = Math.min(BALANCE_TOOLTIP_WIDTH, viewportWidth - margin * 2);
    const iconCenter = rect.left + rect.width / 2;

    // Left-anchor the card near the icon so it opens to the right, then clamp
    // within the viewport — this keeps it off the left sidebar for short names.
    const maxLeft = viewportWidth - width - margin;
    const left = Math.max(margin, Math.min(iconCenter - 24, maxLeft));
    // Keep the caret pointing at the icon, clamped inside the card.
    const caretLeft = Math.max(12, Math.min(iconCenter - left, width - 12));

    // Anchor 8px above the icon; the card is translated up (-100%).
    setTip({ top: rect.top - 8, left, width, caretLeft });
  };

  return (
    <span
      ref={iconRef}
      onMouseEnter={show}
      onMouseLeave={() => setTip(null)}
      className="inline-flex items-center"
    >
      <FaExclamationTriangle
        className="h-3.5 w-3.5 cursor-pointer text-amber-500 dark:text-amber-400"
        aria-label={`Balance warning: ${message}`}
      />
      {tip &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: tip.top,
              left: tip.left,
              transform: "translateY(-100%)",
            }}
            className="pointer-events-none z-[9999]"
          >
            {/* Tooltip card — Figma: 340px wide, 8px radius, 5px padding,
                #0F172A bg, 0/8/16 shadow @ 20%. */}
            <div
              style={{ width: tip.width }}
              className="relative rounded-lg bg-[#0F172A] p-[5px] shadow-[0_8px_16px_0_rgba(0,0,0,0.2)]"
            >
              <p className="m-0 text-left text-[13px] font-normal leading-[18px] tracking-normal text-white">
                {message}
              </p>
              {/* Downward caret (matches the card background), aligned to the icon. */}
              <span
                aria-hidden="true"
                className="absolute top-full -translate-x-1/2"
                style={{
                  left: tip.caretLeft,
                  width: 0,
                  height: 0,
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                  borderTop: "6px solid #0F172A",
                }}
              />
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
};

export default function Leaves() {
  const [leaves, setLeaves] = useState<any>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [showSuccessToaster, setSuccessToaster] = useState(false);
  const [showErrorToaster, setErrorToaster] = useState(false);
  const [message, setMessage] = useState("");
  const [originalLeaves, setOriginalLeaves] = useState<any>([]);
  const [editedRows, setEditedRows] = useState<any>([]);
  // Bulk-approve balance-warning confirmation: modal flag + per-row LOP choice.
  const [bulkWarnOpen, setBulkWarnOpen] = useState(false);
  const [bulkDeductible, setBulkDeductible] = useState<Record<number, boolean>>({});
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedUser, setSelectedUser] = useState<OptionProps | null>(null);
  const [draftSelectedStatus, setDraftSelectedStatus] = useState("");
  const [draftSelectedMonth, setDraftSelectedMonth] = useState("");
  const [draftSelectedYear, setDraftSelectedYear] = useState(
    String(new Date().getFullYear()),
  );
  const [draftSelectedUser, setDraftSelectedUser] = useState<OptionProps | null>(
    null,
  );
  const [userOptions, setUserOptions] = useState<OptionProps[]>([]);
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const USER_PAGE_SIZE = 10;
  const { t } = useTranslation();
  const { user, userRole, email } = useAppContext();

  const statusOptions = [
    { value: "Submitted", label: "Submitted" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
  ];

  const monthOptions = [
    { value: "JANUARY", label: "January" },
    { value: "FEBRUARY", label: "February" },
    { value: "MARCH", label: "March" },
    { value: "APRIL", label: "April" },
    { value: "MAY", label: "May" },
    { value: "JUNE", label: "June" },
    { value: "JULY", label: "July" },
    { value: "AUGUST", label: "August" },
    { value: "SEPTEMBER", label: "September" },
    { value: "OCTOBER", label: "October" },
    { value: "NOVEMBER", label: "November" },
    { value: "DECEMBER", label: "December" },
  ];

  // Generates 11 options: from (Current Year - 10) up to (Current Year)
  // const yearOptions = Array.from({ length: 11 }, (_, i) => {
  //   const year = new Date().getFullYear() - 10 + i;
  //   return { value: String(year), label: String(year) };
  // });

  const currentYear = new Date().getFullYear();

  const yearOptions = Array.from({ length: 12 }, (_, i) => {
    const year = currentYear - 10 + i;
    return {
      value: String(year),
      label: String(year),
    };
  });

  const handleStatusChange = (row: any, newStatus: string) => {
    const originalRow = originalLeaves.find((r: any) => r.id === row.id);

    // Update the leaves state
    const updatedLeaves = leaves.map((leave: any) =>
      leave.id === row.id ? { ...leave, status: newStatus } : leave
    );
    setLeaves(updatedLeaves);

    // Track edited rows
    if (originalRow && originalRow.status !== newStatus) {
      const existingEditIndex = editedRows.findIndex(
        (r: any) => r.id === row.id
      );
      const updatedRow = { ...row, status: newStatus };

      if (existingEditIndex >= 0) {
        // Update existing edited row
        const newEditedRows = [...editedRows];
        newEditedRows[existingEditIndex] = updatedRow;
        setEditedRows(newEditedRows);
      } else {
        // Add new edited row
        setEditedRows([...editedRows, updatedRow]);
      }
    } else if (originalRow && originalRow.status === newStatus) {
      // Remove from edited rows if reverted to original
      setEditedRows(editedRows.filter((r: any) => r.id !== row.id));
    }
  };

  const handleEdit = (row: any): void => {
    setUpdateModal(true);
    setSelectedRow(row);
  };
  const handleDelete = (row: any): void => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleView = (row: any): void => {
    setViewModal(true);
    setSelectedRow(row);
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const onUpdated = () => {
    setOpenModal(false);
    getLeaveData();
    setSuccessToaster(true);
    setMessage(" Leave is Updated Successfully");
  };

  const handleAddBtnClick = () => {
    setAddModal(true);
  };

  const deleteRow = async (id: any) => {
    setDeleteModal(false);
    leaveManager
      .deleteLeave(id)
      .then(async (res) => {
        if (res.status === 200) {
          setSuccessToaster(true);
          setMessage("Leave is Deleted successfully");
          await getLeaveData();
        } else {
          setErrorToaster(true);
          setMessage(res.statusText || "Something went wrong");
        }
      })
      .catch((err) => {
        setErrorToaster(true);
        setMessage(err || "Something went wrong");
        console.log("Error :", err)
      });
  };

  const closeToaster = () => {
    setErrorToaster(false);
    setSuccessToaster(false);
  };

  const tableColumns = [
    // { key: "id", label: `ID`, bold: true },
    {
      key: "username",
      label: ` Name`,
      bold: true,
      // Flag rows with a balance issue so admins notice before approving/rejecting.
      // The amber icon's tooltip carries the backend balanceWarning message.
      render: (value: any, row: any) => {
        const isAdmin = userRole === 2 || userRole === 3;
        const warning: string | null = row?.balanceWarning ?? null;

        return (
          <span className="inline-flex items-center gap-1.5">
            <span>{value}</span>
            {isAdmin && warning && <BalanceWarningBadge message={warning} />}
          </span>
        );
      },
    },
    {
      key: "leaveFrom",
      label: `${t("leave.leaveFrom")}`,
      render: (value: any) => dateFormate(String(value)),
    },
    {
      key: "leaveTo",
      label: `${t("leave.leaveTo")}`,
      render: (value: any) => dateFormate(String(value)),
    },
    { key: "leaveType", label: `${t("leave.leaveType")}` },
    { key: "unit", label: `${t("leave.days")}`, bold: true },
    // { key: "reason", label: `${t("leave.reason")}` },
    { key: "lopDays", label: `LOP`, bold: true },
    {
      key: "status",
      label: `${t("leave.status")}`,
      bold: true,
      render: (value: any, row: any) => {
        const originalRow = originalLeaves.find((r: any) => r.id === row.id);

        // Define status colors
        const getStatusColor = (status: string) => {
          switch (status) {
            case "Submitted":
              return "bg-warning/10 text-warning border-warning/20 dark:bg-warning/20 dark:border-warning/30";
            case "Approved":
              return "bg-success/10 text-success border-success/20 dark:bg-success/20 dark:border-success/30";
            case "Rejected":
              return "bg-error/10 text-error border-error/20 dark:bg-error/20 dark:border-error/30";
            default:
              return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700";
          }
        };

        // Check if user has permission to change status (admin/sys_admin only)
        const canEditStatus = userRole === 2 || userRole === 3;

        if (!canEditStatus) {
          // Display as badge for non-admin users
          return (
            <span
              className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded border ${getStatusColor(value)}`}
            >
              {value}
            </span>
          );
        }

        return (
          <select
            value={row.status}
            onChange={(e) => handleStatusChange(row, e.target.value)}
            className={`border rounded px-2 py-1 text-xs font-medium ${getStatusColor(row.status)}`}
            disabled={
              row.email === email ||
              (originalRow && originalRow.status !== "Submitted")
            }
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      key: "actions",
      label: `${t("leave.actions")}`,
      excludeExport: true,
      actions: (row: any) => (
        <div className="flex flex-start">
          {userRole === 2 &&
            (row?.status === "Submitted") ? (
            <span
              onClick={() => handleEdit(row)}
              className="btn-primary px-2 py-2 mr-2 rounded-lg"
            >
              <FaEdit />
            </span>
          ) : (
            userRole !== 2 &&
            row?.status === "Submitted" && (
              <span
                onClick={() => handleEdit(row)}
                className="btn-primary px-2 py-2 mr-2 rounded-lg"
              >
                <FaEdit />
              </span>
            )
          )}
          <span
            onClick={() => handleView(row)}
            className="btn-primary px-2 py-2 mr-2 rounded-lg"
          >
            <FaEye />
          </span>
          {row?.status === "Submitted" && (
            <span
              onClick={() => handleDelete(row)}
              className="btn-red px-2 py-2 rounded-lg"
            >
              <FaRegTrashAlt />
            </span>
          )}
        </div>
      ),
    },
  ];

  const fetchUsers = async (
    nextPage: number,
    searchValue: string,
    append: boolean
  ) => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(nextPage));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }

      const res = await apiService.get(`api/users?${query.toString()}`);
      const payload = res.data;
      let users: any[] = [];
      if (Array.isArray(payload)) {
        users = payload;
      } else if (Array.isArray(payload?.content)) {
        users = payload.content;
      }

      const typedUsers: UserSummary[] = users;
      const options = typedUsers
        .filter((item) => item.email)
        .map((item) => ({
          value: item.email,
          label: item.displayName || item.email,
        }));

      setUserOptions((prev) => {
        const merged = append ? [...prev, ...options] : options;
        const dedup = new Map<string, OptionProps>();
        merged.forEach((item) => {
          if (!dedup.has(item.value)) {
            dedup.set(item.value, item);
          }
        });
        return sortByKey(Array.from(dedup.values()), "label");
      });

      setUserPage(nextPage);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(nextPage < payload.totalPages - 1);
      } else {
        setHasMoreUsers(options.length === USER_PAGE_SIZE);
      }
    } catch (err) {
      console.log("Failed to fetch users", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (!(userRole === 2 || userRole === 3)) return;

    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [userRole, userSearch]);

  const getUrl = () => {
    const year = Number(selectedYear) || new Date().getFullYear();
    const da = storageService.getItem("user");
    const aaa = da && JSON.parse(da);

    const query = new URLSearchParams();
    query.set("page", String(page));
    query.set("size", String(size));

    if (selectedStatus) {
      query.set("status", selectedStatus);
    }
    if (selectedMonth) {
      query.set("month", selectedMonth);
    }
    if (searchTerm.trim()) {
      query.set("username", searchTerm.trim());
    }

    if (aaa.role === 2 || aaa.role === 3) {
      if (selectedUser?.value) {
        query.set("email", selectedUser.value);
      }
    } else if (aaa.email) {
      query.set("email", aaa.email);
    }

    const queryString = query.toString();
    return queryString
      ? `/api/leave/data/${year}?${queryString}`
      : `/api/leave/data/${year}`;
  };

  const getLeaveData = async () => {
    setLoading(true);
    const url = getUrl();
    console.log("url", url);
    apiService
      .get(url)
      .then((res) => {
        const payload = res.data;
        let data: any[] = [];
        if (Array.isArray(payload)) {
          data = payload;
        } else if (Array.isArray(payload?.content)) {
          data = payload.content;
        } else if (Array.isArray(payload?.data)) {
          data = payload.data;
        }
        const leavesResponses: Row[] = data.map((row: any) => ({
          ...row,
        }));

        leavesResponses.sort((a: any, b: any) => {
          // First priority: Submitted status comes first
          if (a.status === "Submitted" && b.status !== "Submitted") return -1;
          if (a.status !== "Submitted" && b.status === "Submitted") return 1;

          // Second priority: Sort by leaveFrom date (most recent first)
          const dateComparison = b.leaveFrom - a.leaveFrom;
          if (dateComparison !== 0) return dateComparison;

          // Third priority: If same date, sort by creation time or ID (most recent first)
          return (b.id || 0) - (a.id || 0);
        });
        console.log("leavesResponses ", leavesResponses);
        setLeaves(leavesResponses);
        setTotalElements(
          typeof payload?.totalElements === "number"
            ? payload.totalElements
            : leavesResponses.length
        );
        setOriginalLeaves(leavesResponses); // Store original data
        setEditedRows([]); // Reset edited rows
      })
      .catch((error: any) => {
        if (error?.response?.status === 404) {
          setLeaves([]);
          setOriginalLeaves([]);
          setEditedRows([]);
          setTotalElements(0);
          setSuccessToaster(true);
          setMessage("No leaves for selected filter");
          return;
        }

        console.error("Error fetching leave list:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onDataAdded = () => {
    setAddModal(false);
    getLeaveData();
    setSuccessToaster(true);
    setMessage("Leave is Added Successfully");
  };

  // Sends the final bulk payload to the backend.
  const submitBulk = async (rows: any[]) => {
    setLoading(true);
    try {
      const response = await apiService.put(`/api/leave/bulk`, rows);
      if (response.status === 200) {
        setSuccessToaster(true);
        setMessage(`${rows.length} leave(s) status updated successfully`);
        setEditedRows([]);
        await getLeaveData();
      }
    } catch (error) {
      console.error("Error updating leave status:", error);
      setErrorToaster(true);
      setMessage("Failed to update leave status");
    } finally {
      setLoading(false);
    }
  };

  const onApprove = async () => {
    if (editedRows.length === 0) {
      setErrorToaster(true);
      setMessage("No changes to approve");
      return;
    }

    // Any edited rows with a balance warning need an explicit LOP decision —
    // confirm them first (default deductible/LOP = true).
    const flagged = editedRows.filter((r: any) => r?.balanceWarning);
    if (flagged.length > 0) {
      const defaults: Record<number, boolean> = {};
      flagged.forEach((r: any) => {
        defaults[r.id] = true;
      });
      setBulkDeductible(defaults);
      setBulkWarnOpen(true);
      return;
    }

    await submitBulk(editedRows);
  };

  // Confirm from the warning modal — attach each flagged row's LOP choice.
  const confirmBulkApprove = async () => {
    const payload = editedRows.map((r: any) =>
      r?.balanceWarning && Object.prototype.hasOwnProperty.call(bulkDeductible, r.id)
        ? { ...r, isDeductible: bulkDeductible[r.id] }
        : r,
    );
    setBulkWarnOpen(false);
    await submitBulk(payload);
  };

  // Quick action: set every flagged row's LOP choice at once.
  const setAllBulkDeductible = (value: boolean) => {
    setBulkDeductible((prev) => {
      const next: Record<number, boolean> = {};
      Object.keys(prev).forEach((id) => {
        next[Number(id)] = value;
      });
      return next;
    });
  };

  const onError = (errorMsg?: string | any) => {
    setErrorToaster(true);
    setMessage(typeof errorMsg === "string" ? errorMsg : "Something went wrong");
  };

  useEffect(() => {
    getLeaveData();
  }, [
    page,
    size,
    selectedStatus,
    selectedMonth,
    selectedYear,
    selectedUser?.value,
    searchTerm,
  ]);

  const handleUserSearch = (
    inputValue: string,
    meta: InputActionMeta
  ): string => {
    if (meta.action === "input-change") {
      setUserSearch(inputValue);
      return inputValue;
    }

    if (meta.action === "menu-close") {
      return "";
    }

    return inputValue;
  };

  const loadMoreUsers = () => {
    if (hasMoreUsers && !loadingUsers) {
      void fetchUsers(userPage + 1, userSearch, true);
    }
  };

  const leaveFilterFields: CommonFilterField[] = [
    {
      id: "leave-status",
      type: "native-select",
      label: "Status",
      widthClass: "min-w-[150px]",
      queryParam: "status",
      value: draftSelectedStatus,
      options: [
        { value: "", label: "All" },
        ...statusOptions.map((opt) => ({ value: opt.value, label: opt.label })),
      ],
      onChange: (value) => {
        setDraftSelectedStatus(value);
      },
    },
    {
      id: "leave-year",
      type: "native-select",
      label: "Year",
      widthClass: "min-w-[130px]",
      queryParam: "year",
      value: draftSelectedYear,
      isActive: draftSelectedYear !== String(new Date().getFullYear()),
      options: yearOptions,
      onChange: (value) => {
        setDraftSelectedYear(value);
      },
    },
    {
      id: "leave-month",
      type: "native-select",
      label: "Month",
      widthClass: "min-w-[160px]",
      queryParam: "month",
      value: draftSelectedMonth,
      options: [
        { value: "", label: "All Months" },
        ...monthOptions.map((opt) => ({ value: opt.value, label: opt.label })),
      ],
      onChange: (value) => {
        setDraftSelectedMonth(value);
      },
    },
    ...((userRole === 2 || userRole === 3
      ? [
        {
          id: "leave-user",
          type: "select" as const,
          label: "User",
          placeholder: "All Users",
          widthClass: "min-w-[230px]",
          queryParam: "email",
          options: userOptions as CommonFilterOption[],
          value: draftSelectedUser as CommonFilterOption | null,
          isClearable: true,
          isSearchable: true,
          isLoading: loadingUsers,
          onInputChange: handleUserSearch,
          onMenuScrollToBottom: loadMoreUsers,
          noOptionsMessage: () =>
            loadingUsers ? "Loading users..." : "No users found",
          onChange: (option: CommonFilterOption | null) => {
            setDraftSelectedUser(option);
          },
        },
      ]
      : []) as CommonFilterField[]),
  ];

  const leaveFilterControls = (
    <CommonListingFilter
      className="leave-listing-filter"
      fields={leaveFilterFields}
      panelTitle="Leave Filter"
      onApply={() => {
        setSelectedStatus(draftSelectedStatus);
        setSelectedMonth(draftSelectedMonth);
        setSelectedYear(draftSelectedYear);
        setSelectedUser(draftSelectedUser);
        setPage(0);
      }}
      onClear={() => {
        setDraftSelectedStatus("");
        setDraftSelectedMonth("");
        setDraftSelectedYear(String(new Date().getFullYear()));
        setDraftSelectedUser(null);
      }}
      onReset={() => {
        const defaultYear = String(new Date().getFullYear());
        setDraftSelectedStatus("");
        setDraftSelectedMonth("");
        setDraftSelectedYear(defaultYear);
        setDraftSelectedUser(null);
        setSelectedStatus("");
        setSelectedMonth("");
        setSelectedYear(defaultYear);
        setSelectedUser(null);
        setPage(0);
      }}
    />
  );
  const handleLeavePageChange = useCallback((nextPage: number) => {
    setPage(nextPage - 1);
  }, []);

  const handleLeaveItemsPerPageChange = useCallback((nextSize: number) => {
    setSize((prev) => (prev === nextSize ? prev : nextSize));
    setPage(0);
  }, []);

  const handleLeaveSearchTermChange = useCallback((nextSearchTerm: string) => {
    setSearchTerm((prev) => (prev === nextSearchTerm ? prev : nextSearchTerm));
    setPage(0);
  }, []);

  const handleExport = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const year = Number(selectedYear) || new Date().getFullYear();
      const da = storageService.getItem("user");
      const aaa = da && JSON.parse(da);

      const query = new URLSearchParams();
      if (selectedStatus) query.set("status", selectedStatus);
      if (selectedMonth) query.set("month", selectedMonth);
      if (searchTerm.trim()) query.set("username", searchTerm.trim());

      if (aaa?.role === 2 || aaa?.role === 3) {
        if (selectedUser?.value) {
          query.set("email", selectedUser.value);
        }
      } else if (aaa?.email) {
        query.set("email", aaa.email);
      }

      // Cache-buster
      query.set("_t", Date.now().toString());

      const queryString = query.toString();
      const url = `/api/leave/exportLeaves/${year}?${queryString}`;

      // 1. Fetch the raw binary data
      const response = await apiService.get(url, { responseType: "arraybuffer" });

      let arrayBuffer;
      if (response.data instanceof ArrayBuffer) {
        arrayBuffer = response.data;
      } else if (response.data instanceof Blob) {
        arrayBuffer = await response.data.arrayBuffer();
      } else {
        const blob = new Blob([response.data]);
        arrayBuffer = await blob.arrayBuffer();
      }

      // 2. Read the workbook
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // 3. Force column widths so they don't show ########
      worksheet["!cols"] = [
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 10 }, { wch: 10 }, { wch: 35 }, { wch: 35 },
        { wch: 18 }, { wch: 25 },
      ];

      // 4. Dynamically find date columns and format them
      if (worksheet["!ref"]) {
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        const dateColIndexes: number[] = [];

        // Scan the header row (row 0) to find columns related to dates
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })];
          if (headerCell && headerCell.v) {
            const header = String(headerCell.v).toLowerCase();
            if (
              header.includes("leavefrom") ||
              header.includes("leaveto") ||
              header.includes("date")
            ) {
              dateColIndexes.push(C);
            }
          }
        }

        // Apply 'dd-MMM-yyyy' formatting to the numbers in those columns
        for (let R = 1; R <= range.e.r; ++R) {
          for (const C of dateColIndexes) {
            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = worksheet[cellAddress];

            // If the cell exists and is a number type ('n' = Excel serial date)
            if (cell && cell.t === "n") {
              cell.z = "dd-MMM-yyyy"; // Format as 16-Jul-2026
            }
          }
        }
      }

      // 5. Package and download
      const exportFileName = `Leaves_List_${year}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(workbook, exportFileName);

      setSuccessToaster(true);
      setMessage("Leaves exported successfully");
    } catch (error) {
      console.error("Failed to export leaves data", error);
      setErrorToaster(true);
      setMessage("Failed to export leaves. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* <BreadCrumb BreadCrumbList={BreadCrumbList} /> */}
      <LeavesList
        data={leaves}
        columns={tableColumns}
        loading={loading}
        onAdd={handleAddBtnClick}
        leftSideButtons={leaveFilterControls}
        itemsPerPage={size}
        currentPage={page + 1}
        totalItems={totalElements}
        onPageChange={handleLeavePageChange}
        onItemsPerPageChange={handleLeaveItemsPerPageChange}
        onSearchTermChange={handleLeaveSearchTermChange}
        preAddButtons={
          <div className="flex items-center gap-3 ">
            <button
              onClick={handleExport} // Your XLSX export function in the parent
              title="Export Leaves"
              type="button"
              className="text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm font-medium rounded-lg text-sm px-4 py-2.5 inline-flex items-center border-radius-0.5rem"
            >
              <AiOutlineExport size={18} className="mr-2" />
              <span>Export</span>
            </button>
          </div>
        }
        customButton={
          (userRole === 2 || userRole === 3) && (
            <HeaderButtons
              onClick={onApprove}
              className={`${editedRows.length === 0
                ? "opacity-50 cursor-not-allowed bg-slate-300 dark:bg-slate-800 text-slate-500"
                : "bg-primary hover:bg-primary-hover shadow-lg shadow-primary/20"
                } transition-all duration-300 rounded-lg px-5 py-2.5 text-sm !font-medium whitespace-nowrap`}
              disabled={editedRows.length === 0 || loading}
            >
              Approve ({editedRows.length})
            </HeaderButtons>
          )
        }
        buttonContent="Approve Selected Leaves"
      />
      {updateModal && (
        <Modal
          isOpen={updateModal}
          onClose={() => setUpdateModal(false)}
          header="Update Leave"
          customWidth="700px"
        >
          <LeaveDetails
            onUpdate={onUpdated}
            selectedRow={selectedRow}
            onClose={() => setUpdateModal(false)}
            onError={onError}
          />
        </Modal>
      )}
      {deleteModal && (
        <GenericModal
          isOpen={deleteModal}
          onClose={() => setDeleteModal(false)}
          header="Delete Leave"
        >
          <DeletePopup
            handleDelete={deleteRow}
            onClose={() => setDeleteModal(false)}
            selectedRow={selectedRow}
          />
        </GenericModal>
      )}
      {addModal && (
        <GenericModal
          isOpen={addModal}
          onClose={() => setAddModal(false)}
          header="Apply for Leave"
          customWidth="700px"
        >
          <AddLeave
            onSuccess={onDataAdded}
            onError={onError}
            onClose={() => setAddModal(false)}
          />
        </GenericModal>
      )}
      {viewModal && (
        <GenericModal
          isOpen={viewModal}
          onClose={() => setViewModal(false)}
          header="Leave Details"
        >
          <ViewLeave selectedRow={selectedRow} />
        </GenericModal>
      )}
      {bulkWarnOpen && (
        <GenericModal
          isOpen={bulkWarnOpen}
          onClose={() => setBulkWarnOpen(false)}
          header="Balance warning — confirm Loss of Pay"
          customWidth="560px"
        >
          <div className="bg-white dark:bg-gray-800">
            {(() => {
              const flaggedRows = editedRows.filter((r: any) => r?.balanceWarning);
              return (
                <>
                  <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
                    {flaggedRows.length} of the selected request
                    {flaggedRows.length === 1 ? "" : "s"} exceed the available balance.
                    Choose whether each should be treated as <span className="font-semibold">Loss of Pay</span> (deductible).
                    It is on by default — change any individually, or apply to all.
                  </p>

                  <div className="max-h-[320px] divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 dark:divide-slate-700/70 dark:border-slate-700">
                    {flaggedRows.map((r: any) => {
                      const on = !!bulkDeductible[r.id];
                      return (
                        <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                              {r.username}
                            </p>
                            <p className="truncate text-xs text-amber-600 dark:text-amber-400">
                              {r.balanceWarning}
                            </p>
                            <p className="truncate text-[11px] text-slate-400 dark:text-slate-500">
                              {r.leaveType} · {r.unit} day{Number(r.unit) === 1 ? "" : "s"} · {r.status}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <span
                              className={`w-8 text-right text-xs font-semibold ${on ? "text-amber-600 dark:text-amber-400" : "text-slate-400 dark:text-slate-500"}`}
                            >
                              {on ? "LOP" : "Paid"}
                            </span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={on}
                              aria-label={`Loss of Pay for ${r.username}`}
                              onClick={() =>
                                setBulkDeductible((prev) => ({ ...prev, [r.id]: !prev[r.id] }))
                              }
                              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${on ? "!bg-amber-500 border-amber-500" : "!bg-slate-200 border-slate-300 dark:!bg-slate-600 dark:border-slate-500"}`}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full !bg-white shadow-sm ring-1 ring-black/5 transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`}
                              />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3 text-xs font-semibold">
                      <button
                        type="button"
                        onClick={() => setAllBulkDeductible(true)}
                        className="text-amber-700 hover:underline dark:text-amber-400"
                      >
                        Mark all as LOP
                      </button>
                      <span className="text-slate-300 dark:text-slate-600">|</span>
                      <button
                        type="button"
                        onClick={() => setAllBulkDeductible(false)}
                        className="text-slate-600 hover:underline dark:text-slate-300"
                      >
                        Mark all as Paid
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setBulkWarnOpen(false)}
                        className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={confirmBulkApprove}
                        disabled={loading}
                        className="rounded-lg bg-blue-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-700"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </GenericModal>
      )}
      {(showSuccessToaster || showErrorToaster) && (
        <GenericToaster
          message={message}
          onClose={closeToaster}
          open={showSuccessToaster || showErrorToaster}
          severity={showErrorToaster ? "error" : "info"}
        />
      )}
    </>
  );
}
